import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { TFunction, useTranslation } from "react-i18next";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { ApiError } from "go-core/api/types";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { ApplicationApi, ItemOrderType } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";
import { AppState } from "../../../utils/types";
import {
	getAcceptanceTimeFromNumberOfSeconds,
	getNumberOfSecondsFromAcceptanceTime,
	getPriceListIdDefaultValue,
	parseApplicationSettings,
	parseApplicationSettingsValues,
	parseStringToList,
} from "../../../utils/utils";
import GoOrderAdvancedForm from "./GoOrderAdvancedForm";
import GoOrderMenuErrors from "./GoOrderMenuErrors";
import GoOrderSendingMenuForm from "./GoOrderSendingMenuForm";
import GoOrderShopConfigurationForm from "./GoOrderShopConfigurationForm";
import GoOrderSynchronizationForm from "./GoOrderSynchronizationForm";
import { getDefaultShowAdvancedSectionForGoOrder } from "./utils";

interface Props {
	appState: AppState;
	handleUpdateApp: (data: ApplicationApi) => void;
	fetchApplication: () => void;
}

const getFormOrderTypes = (t: TFunction, menuInfo: Record<string, any>) => {
	const result: ItemOrderType[] = [];

	if (menuInfo.pick_up) {
		result.push(ItemOrderType.PICK_UP);
	}
	if (menuInfo.dine_in) {
		result.push(ItemOrderType.DINE_IN);
	}
	if (menuInfo.delivery) {
		result.push(ItemOrderType.DELIVERY);
	}
	if (menuInfo.room_service) {
		result.push(ItemOrderType.ROOM_SERVICE);
	}

	return result;
};

const getParsedMenusToForm = (t: TFunction, menuInfos?: string) => {
	if (!menuInfos || JSON.parse(menuInfos)?.length === 0)
		return [
			{
				menu_id: undefined,
				availability_id: undefined,
				order_types: [],
			},
		];

	const parsedMenuInfos = JSON.parse(menuInfos);

	return parsedMenuInfos.map((menuInfo: Record<string, any>) => ({
		...menuInfo,
		order_types: getFormOrderTypes(t, menuInfo),
	}));
};

const GoOrderFormModalCard = ({ appState, handleUpdateApp, fetchApplication }: Props) => {
	const { t } = useTranslation();
	const [updatedConfig, setUpdatedConfig] = useState<Record<string, any> | undefined>(appState.config);
	const [menuErrors, setMenuErrors] = useState<ApiError[]>([]);
	const { addFlash, addSuccessFlash } = useFlash();
	const formDefaultValues = {
		...appState.app,
		settings: {
			...appState.app.settings,
			...parseApplicationSettingsValues(appState.app.settings),
			custom_fields_to_sync: parseStringToList(
				appState.app.settings,
				updatedConfig || {},
				"custom_fields_to_sync",
				"custom_fields"
			)?.map((cf) => ({
				label: cf?.label ?? cf?.name,
				id: cf?.slug,
			})),
			notification_terminal: parseStringToList(
				appState.app.settings,
				updatedConfig || {},
				"notification_terminal",
				"notification_terminals"
			)?.map((terminal) => ({
				label: terminal?.name,
				id: terminal?.id,
			})),
			external_store_id: parseStringToList(
				appState.app.settings,
				updatedConfig || {},
				"external_store_id",
				"stores"
			)?.map((store) => ({
				label: store?.name,
				id: store?.id,
			})),
			time_unit: getAcceptanceTimeFromNumberOfSeconds(appState.app.settings.order_acceptance_time_seconds)
				?.timeUnit,
			number_of_time_units: getAcceptanceTimeFromNumberOfSeconds(
				appState.app.settings.order_acceptance_time_seconds
			)?.numberOfTimeUnits,
			price_list_id: getPriceListIdDefaultValue(appState, updatedConfig, true),
			menu_infos: getParsedMenusToForm(t, appState.app.settings.menu_infos),
		} as Record<string, any>,
	};
	const form = useForm<ApplicationApi>({
		criteriaMode: "all",
		defaultValues: formDefaultValues,
	});
	const {
		register,
		watch,
		handleSubmit,
		formState: { errors },
		reset,
		formState,
		setError,
	} = form;
	const [loading, setLoading] = useState(false);
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const [showAdvancedSection, setShowAdvancedSection] = useState(
		getDefaultShowAdvancedSectionForGoOrder(
			formDefaultValues.settings.auto_link_by_name,
			formDefaultValues.settings.external_delivery,
			formDefaultValues.settings.item_multi_level,
			formDefaultValues.settings.external_store_id,
			formDefaultValues.settings.notification_terminal,
			formDefaultValues.settings.custom_fields_to_sync,
			formDefaultValues.settings.point_of_sale_id,
			formDefaultValues.settings.number_of_time_units,
			formDefaultValues.settings.time_unit,
			formDefaultValues.settings.order_accept_type,
			formDefaultValues.settings.image_tag
		)
	);

	const isOrderTypeIncluded = (orderType: ItemOrderType, orderTypes: ItemOrderType[]) =>
		(orderTypes || []).includes(orderType);

	const getParsedDataForm = (data: ApplicationApi) => {
		const settings = parseApplicationSettings(data.settings, ["menu_infos"]);
		const order_acceptance_time_seconds = getNumberOfSecondsFromAcceptanceTime(
			settings.number_of_time_units,
			settings.time_unit
		);

		delete settings.time_unit;
		delete settings.number_of_time_units;

		const { price_list_id } = settings;
		let priceListIdValue: string = "";

		if (Array.isArray(price_list_id)) {
			priceListIdValue = price_list_id.map((priceListId) => priceListId.id).join(",");
		} else if (typeof price_list_id === "string") {
			priceListIdValue = price_list_id;
		}

		let menuInfos: Record<string, any>[] = settings.menu_infos.filter(
			(menuInfo: Record<string, any>) =>
				menuInfo.menu_id || menuInfo.availability_id || menuInfo.order_types?.length > 0
		);
		menuInfos = menuInfos.map((menuInfo) => ({
			...menuInfo,
			pick_up: isOrderTypeIncluded(ItemOrderType.PICK_UP, menuInfo.order_types),
			delivery: isOrderTypeIncluded(ItemOrderType.DELIVERY, menuInfo.order_types),
			room_service: isOrderTypeIncluded(ItemOrderType.ROOM_SERVICE, menuInfo.order_types),
			dine_in: isOrderTypeIncluded(ItemOrderType.DINE_IN, menuInfo.order_types),
		}));
		menuInfos.forEach((menuInfo) => {
			delete menuInfo.order_types;
		});

		return {
			name: data.name,
			id: appState.app.id,
			settings: {
				...settings,
				order_acceptance_time_seconds,
				price_list_id: priceListIdValue,
				menu_infos: JSON.stringify(menuInfos),
			},
		};
	};

	const onSubmit = handleSubmit(async (data: ApplicationApi) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		const params: Record<string, any> = { include: "settings" };
		const newData = getParsedDataForm(data);
		try {
			const res = await api.organization().updateApp(newData, params);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));

			handleUpdateApp(res);
			reset(watch());
			setShowAdvancedSection(
				getDefaultShowAdvancedSectionForGoOrder(
					watch("settings").auto_link_by_name,
					watch("settings").external_delivery,
					watch("settings").item_multi_level,
					watch("settings").external_store_id,
					watch("settings").notification_terminal,
					watch("settings").custom_fields_to_sync,
					watch("settings").point_of_sale_id,
					watch("settings").number_of_time_units,
					watch("settings").time_unit,
					watch("settings").order_accept_type,
					watch("settings").image_tag
				)
			);
		} catch (e) {
			handleError.form(e, setError, addFlash);
		}
		setLoading(false);
	});

	const updateConfig = (prop: string, obj?: any) => {
		let newObj;
		if (Array.isArray(obj)) {
			newObj = obj.map((o: Record<string, any>) => {
				return {
					name: o.name,
					id: o.id,
				};
			});
		} else {
			newObj = obj !== undefined ? { ...obj } : obj;
		}

		const cnf = { ...updatedConfig };
		if (prop === "stores") {
			cnf.stores = newObj;
		}
		if (prop === "notification_terminals") {
			cnf.notification_terminals = newObj;
		}
		if (prop === "menu_name") {
			cnf.menu_name = newObj?.label;
		}
		if (prop === "payment_method_name") {
			cnf.payment_method_name = newObj?.label;
		}

		setUpdatedConfig(cnf);
	};

	return (
		<Card className="go-order-app-form-card">
			<Card.Body>
				<FormDirty formState={formState} onSubmit={onSubmit} className={"app-form"}>
					<FormInput label={t("lib:common.word.name")} register={register} name="name" errors={errors} />
					<GoOrderSendingMenuForm
						form={form}
						updatedConfig={updatedConfig}
						setUpdatedConfig={setUpdatedConfig}
						appState={appState}
					/>
					<GoOrderShopConfigurationForm
						form={form}
						updatedConfig={updatedConfig}
						appState={appState}
						updateConfig={updateConfig}
						setErrors={setErrors}
					/>
					<GoOrderSynchronizationForm
						form={form}
						appState={appState}
						fetchApplication={fetchApplication}
						setMenuErrors={setMenuErrors}
					/>
					<GoOrderAdvancedForm
						form={form}
						appState={appState}
						updateConfig={updateConfig}
						updatedConfig={updatedConfig}
						setErrors={setErrors}
						showAdvancedSection={showAdvancedSection}
						setShowAdvancedSection={setShowAdvancedSection}
					/>
					<GoOrderMenuErrors menuErrors={menuErrors} />
					<ButtonLoading loading={loading} variant={"add"} onClick={onSubmit}>
						{t("common.action.save", { ns: "lib" })}
					</ButtonLoading>
				</FormDirty>
			</Card.Body>
		</Card>
	);
};
export default GoOrderFormModalCard;
